import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import KYCStepIndicator from '../components/kyc/KYCStepIndicator';
import AuthenticationStep from '../components/kyc/AuthenticationStep';
import PersonalInfoStep from '../components/kyc/PersonalInfoStep';
import BankDetailsStep from '../components/kyc/BankDetailsStep';
import DocumentUploadStep from '../components/kyc/DocumentUploadStep';
import SuccessStep from '../components/kyc/SuccessStep';
import useKYCStore from '../store/kycStore';
import { checkKYCStatus, getShopInfo, ShopInfo } from '../lib/supabase/api/update-kyc-info';
import { FiCheckCircle, FiLoader, FiAlertCircle } from 'react-icons/fi';
import IconWrapper from '../components/print-steps/IconWrapper';
 
const KYCFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { 
    currentStep, 
    totalSteps,
    setCurrentStep,
    isAuthenticated,
    shopAccountId,
    isSubmitting,
    isLoading,
    setIsLoading,
    clearStore,
  } = useKYCStore();
  
  const [prevStep, setPrevStep] = useState(currentStep);
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const [transitioning, setTransitioning] = useState(false);
  const [kycStatus, setKycStatus] = useState<string | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [shopInfo, setShopInfo] = useState<ShopInfo | null>(null);

  // Check KYC status when authenticated
  useEffect(() => {
    const fetchKYCStatus = async () => {
      if (isAuthenticated && shopAccountId) {
        setStatusLoading(true);
        try {
          // Fetch both KYC status and shop info
          const [status, info] = await Promise.all([
            checkKYCStatus(shopAccountId),
            getShopInfo(shopAccountId)
          ]);
          
          console.log('KYC Status:', status);
          console.log('Shop Info:', info);
          setKycStatus(status);
          setShopInfo(info);
        } catch (error) {
          console.error('Error fetching KYC status:', error);
        } finally {
          setStatusLoading(false);
        }
      }
    };
    
    fetchKYCStatus();
  }, [isAuthenticated, shopAccountId]);

  // Track direction changes for animations
  useEffect(() => {
    if (currentStep !== prevStep) {
      const isForward = currentStep > prevStep;
      setDirection(isForward ? 'forward' : 'backward');
      setTransitioning(true);
      
      const timer = setTimeout(() => {
        setTransitioning(false);
        setPrevStep(currentStep);
      }, 300); // Match with CSS transition duration
      
      return () => clearTimeout(timer);
    }
  }, [currentStep, prevStep]);

  // Get the animation class based on direction
  const getAnimationClass = () => {
    if (!transitioning) return '';
    return direction === 'forward' ? 'slide-from-right' : 'slide-from-left';
  };

  // Render KYC status message
  const renderKYCStatus = () => {
    if (statusLoading) {
      return (
        <div className="rounded-md bg-blue-50 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0 animate-spin">
              <IconWrapper icon={FiLoader} className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-blue-800">
                Checking KYC status...
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (kycStatus === 'submitted') {
      return (
        <div className="rounded-md bg-yellow-50 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <IconWrapper icon={FiAlertCircle} className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-yellow-800">KYC Already Submitted</h3>
              {shopInfo && (
                <div className="mt-1 text-sm text-yellow-700">
                  <p><span className="font-medium">Shop:</span> {shopInfo.shop_name}</p>
                  <p><span className="font-medium">Owner:</span> {shopInfo.owner_name}</p>
                </div>
              )}
              <div className="mt-2 text-sm text-yellow-700">
                <p>Your KYC documents have been submitted and are currently under review.</p>
                <p className="mt-1">Please check back later for updates on your verification status.</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => {
                    clearStore();
                    setKycStatus(null);
                    setShopInfo(null);
                    setCurrentStep(0); // Reset to authentication step
                    window.location.reload();
                  }}
                  className="inline-flex items-center px-3 py-2 border border-yellow-300 text-sm font-medium rounded-md text-yellow-800 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors duration-200"
                >
                  Register Different Shop
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (kycStatus === 'verified') {
      return (
        <div className="rounded-md bg-green-50 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <IconWrapper icon={FiCheckCircle} className="h-5 w-5 text-green-400" />
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-green-800">KYC Verification Complete</h3>
              {shopInfo && (
                <div className="mt-1 text-sm text-green-700">
                  <p><span className="font-medium">Shop:</span> {shopInfo.shop_name}</p>
                  <p><span className="font-medium">Owner:</span> {shopInfo.owner_name}</p>
                </div>
              )}
              <div className="mt-2 text-sm text-green-700">
                <p>Your KYC verification has been completed successfully.</p>
                <p className="mt-1">Your shop is now ready to accept print orders!</p>
              </div>
              <div className="mt-4 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
                  onClick={() => navigate('/')}
                >
                  Go to Dashboard
                </button>
                <button
                  onClick={() => {
                    clearStore();
                    setKycStatus(null);
                    setShopInfo(null);
                    setCurrentStep(0); // Reset to authentication step
                    window.location.reload();
                  }}
                  className="inline-flex items-center px-4 py-2 border border-green-300 text-sm font-medium rounded-md text-green-800 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
                >
                  Register Different Shop
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // KYC not submitted or unknown status - show form
    return null;
  };

  // Render the current step
  const renderCurrentStep = () => {
    // If KYC is already submitted or verified, don't show the form
    if (isAuthenticated && (kycStatus === 'submitted' || kycStatus === 'verified')) {
      return null;
    }
    
    // If not authenticated and not on the first step, redirect to first step
    if (!isAuthenticated && currentStep !== 0) {
      console.log('Not authenticated, redirecting to step 0');
      setCurrentStep(0);
    }
    
    console.log('Rendering step:', currentStep, 'totalSteps:', totalSteps);
    
    // Ensure the success step is shown when currentStep === 4
    if (currentStep === 4) {
      console.log('Rendering success step explicitly');
      return <SuccessStep />;
    }
    
    switch(currentStep) {
      case 0:
        return <AuthenticationStep />;
      case 1:
        return <PersonalInfoStep />;
      case 2:
        return <BankDetailsStep />;
      case 3:
        return <DocumentUploadStep />;
      default:
        console.warn('Unknown step:', currentStep);
        // Fallback to success step if step is beyond range
        if (currentStep > 3) {
          console.log('Fallback to success step');
          return <SuccessStep />;
        }
        return <AuthenticationStep />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-grow container mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">

            {/* Show KYC status if available */}
            {renderKYCStatus()}
            
            {/* Only show step indicator and form if KYC not submitted/verified */}
            {(!isAuthenticated || (kycStatus !== 'submitted' && kycStatus !== 'verified')) && (
              <>
                {/* Only show step indicator after authentication */}
                {isAuthenticated && currentStep < 4 && (
                  <KYCStepIndicator />
                )}
                
                <div className="relative overflow-hidden mt-4 sm:mt-8">
                  <div className={`step-container ${getAnimationClass()}`}>
                    {renderCurrentStep()}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default KYCFormPage;
